// ── Voice Input ──
// Keyboard handler for push-to-talk voice chat.
// Listens for a configurable PTT key (default: CapsLock) and
// triggers startTransmitting / stopTransmitting on the VoiceChatManager.

import { voiceChat } from '../audio/voice';
import { keybindManager } from '../core/keybinds';

export class VoiceInput {
  private pttKey: string;
  private _active = false;
  private enabled = false;
  private _ownKey: boolean;
  private keybindListener: (() => void) | null = null;

  private boundKeyDown: (e: KeyboardEvent) => void;
  private boundKeyUp: (e: KeyboardEvent) => void;
  private boundBlur: () => void;

  constructor(pttKey?: string) {
    // If no explicit key provided, follow keybindManager's 'pushToTalk' binding.
    this._ownKey = pttKey !== undefined;
    this.pttKey = pttKey ?? keybindManager.getKeyForAction('pushToTalk') ?? 'CapsLock';

    if (!this._ownKey) {
      this.keybindListener = () => {
        const newKey = keybindManager.getKeyForAction('pushToTalk') ?? 'CapsLock';
        if (newKey !== this.pttKey) {
          // G-M-005: unconditionally release the mic on key change. The old
          // guard skipped stopTransmitting when _active was false, which
          // left a stuck-mic hole if any async path (preflight resolving
          // after rebind, voice.setMuted toggling) had quietly armed the
          // WebRTC track even though _active was clear. stopTransmitting()
          // is idempotent on VoiceChatManager so the unconditional call is
          // a harmless safety net when nothing was active.
          this._active = false;
          voiceChat.stopTransmitting();
          this.pttKey = newKey;
        }
      };
      keybindManager.onChange(this.keybindListener);
    }

    this.boundKeyDown = (e: KeyboardEvent) => {
      if (e.code !== this.pttKey) return;
      // Guard against key repeat (held key fires multiple keydown events)
      if (this._active) return;
      this._active = true;
      // A-M-006: synchronously mark the transmit state as 'warming' so any
      // VAD analyser ticks during the preflight gap suppress speaking=true
      // events. The HUD's LIVE indicator is gated on state === 'live' and
      // therefore won't flash before the mic actually goes live.
      voiceChat.setTransmitState?.('warming');
      // F-MOB-M4: run the PTT preflight (mic permission + AudioContext unlock)
      // and only call startTransmitting on 'ok'. If denied or no track, the
      // active state is rolled back so the next press tries again.
      void voiceChat.preflightPTT().then((res) => {
        if (!this._active) {
          // Released or rebound mid-preflight — make sure state drops to idle.
          voiceChat.setTransmitState?.('idle');
          return;
        }
        if (res === 'ok') {
          voiceChat.startTransmitting();
        } else {
          this._active = false;
          voiceChat.setTransmitState?.('idle');
        }
      });
    };

    // G-M-005: gate on current pttKey + _active. After a mid-press rebind
    // the stale-key keyup arrives with the old code — and pttKey now holds
    // the NEW code, so this comparison naturally drops the stale event.
    // _active was already cleared by keybindListener at rebind time.
    this.boundKeyUp = (e: KeyboardEvent) => {
      if (!this._active || e.code !== this.pttKey) return;
      this._active = false;
      voiceChat.stopTransmitting();
    };

    // AUD-B3 / AUD-I4: when the window loses focus (alt-tab, click on another
    // window, Cmd+Tab) the keyup never fires for the held PTT key, leaving the
    // mic transmitting in the background. Releasing on blur is idempotent —
    // safe to fire when not transmitting.
    this.boundBlur = () => {
      if (!this._active) return;
      this._active = false;
      voiceChat.stopTransmitting();
    };
  }

  /** Whether PTT is currently held. */
  isActive(): boolean {
    return this._active;
  }

  /** Get the current PTT key code. */
  getPTTKey(): string {
    return this.pttKey;
  }

  /** Change the PTT key at runtime. Also pins this instance to an explicit key (stops tracking keybindManager). */
  setPTTKey(key: string): void {
    this.pttKey = key;
    this._ownKey = true;
    if (this.keybindListener) {
      keybindManager.offChange(this.keybindListener);
      this.keybindListener = null;
    }
    // Reset active state since key changed
    if (this._active) {
      this._active = false;
      voiceChat.stopTransmitting();
    }
  }

  /** Start listening for PTT key events. */
  enable(): void {
    if (this.enabled) return;
    this.enabled = true;
    document.addEventListener('keydown', this.boundKeyDown);
    document.addEventListener('keyup', this.boundKeyUp);
    window.addEventListener('blur', this.boundBlur);
  }

  /** Stop listening for PTT key events. */
  disable(): void {
    if (!this.enabled) return;
    this.enabled = false;
    document.removeEventListener('keydown', this.boundKeyDown);
    document.removeEventListener('keyup', this.boundKeyUp);
    window.removeEventListener('blur', this.boundBlur);
    if (this._active) {
      this._active = false;
      voiceChat.stopTransmitting();
    }
  }

  /** Remove all listeners and reset state. */
  dispose(): void {
    this.disable();
    this._active = false;
    if (this.keybindListener) {
      keybindManager.offChange(this.keybindListener);
      this.keybindListener = null;
    }
  }
}

/** Singleton voice input handler. */
export const voiceInput = new VoiceInput();
